/**
 * Turns a plan into the before/after list the review panel shows.
 *
 * Transparency level 2 in PLANT.md §9.6: the exact control, its value before
 * and after, the scope, and the reason. No proposal is accepted from a
 * summary alone.
 */

import type { EditPlan, PlanChange, PlanOperation } from './plan.ts';
import type { Recipe } from './recipe.ts';
import { OPERATIONS_BY_ID, type ScalarOperationSpec, readPath } from './registry.ts';
import { describeCurve } from './curve.ts';

function fallbackValue(id: string, neutral: number, context: DescribeContext): number {
  if (!context.asShot) return neutral;
  if (id === 'temperature') return context.asShot.kelvin;
  if (id === 'tint') return context.asShot.tint;
  return neutral;
}

export function formatScalar(spec: ScalarOperationSpec, value: number): string {
  const decimals = spec.step < 0.1 ? 2 : spec.step < 1 ? 1 : 0;
  const text = value.toFixed(decimals);
  const signed = spec.neutral === 0 && value > 0 ? `+${text}` : text;
  return spec.unit ? `${signed}${spec.unit === '°' || spec.unit === 'K' ? '' : ' '}${spec.unit}` : signed;
}

/**
 * Facts the recipe alone cannot supply.
 *
 * Temperature and tint are `null` until the user moves them, meaning "as shot".
 * Without the camera's own numbers the before-value would read as the registry
 * neutral — a plan that warms a 6800 K frame to 7600 K would claim it had moved
 * it from 5500 K, which is exactly the sort of quiet inaccuracy this panel
 * exists to prevent.
 */
export interface DescribeContext {
  asShot?: { kelvin: number; tint: number };
}

export function describePlan(
  recipe: Recipe,
  plan: EditPlan,
  context: DescribeContext = {},
): PlanChange[] {
  return plan.operations.map((operation) => describeOperation(recipe, operation, context));
}

export function describeOperation(
  recipe: Recipe,
  operation: PlanOperation,
  context: DescribeContext = {},
): PlanChange {
  const spec = OPERATIONS_BY_ID.get(operation.op);

  if (operation.kind === 'scalar' && spec?.kind === 'scalar') {
    const currentRaw = readPath(recipe, spec.path);
    const current =
      typeof currentRaw === 'number' ? currentRaw : fallbackValue(spec.id, spec.neutral, context);
    return {
      operationId: operation.op,
      label: spec.label,
      group: spec.group,
      before: formatScalar(spec, current),
      after: formatScalar(spec, operation.value),
      reason: operation.reason,
    };
  }

  if (operation.kind === 'curve' && spec?.kind === 'curve') {
    const currentRaw = readPath(recipe, spec.path);
    const current = Array.isArray(currentRaw) ? currentRaw : [];
    return {
      operationId: operation.op,
      label: spec.label,
      group: spec.group,
      before: describeCurve(current as { x: number; y: number }[]),
      after: describeCurve(operation.points),
      reason: operation.reason,
      detail: `${operation.points.length} points`,
    };
  }

  if (operation.kind === 'mask') {
    const active = Object.entries(operation.adjustments)
      .filter(([, value]) => typeof value === 'number' && value !== 0)
      .map(([key, value]) => `${key} ${(value as number) > 0 ? '+' : ''}${value}`)
      .join(', ');
    return {
      operationId: operation.op,
      label: operation.name,
      group: 'subject',
      before: 'none',
      after: `${operation.maskKind} mask`,
      reason: operation.reason,
      detail: active || 'no adjustments',
    };
  }

  return {
    operationId: operation.op,
    label: operation.op,
    group: 'look',
    before: 'unknown',
    after: 'unknown',
    reason: 'Unrecognised operation.',
  };
}

/**
 * A compact description of the current recipe for the planner prompt: only the
 * controls that are actually off their neutral value, so the model sees the
 * edit history rather than a wall of zeroes.
 */
export function describeActiveRecipe(recipe: Recipe): Record<string, unknown> {
  const active: Record<string, unknown> = {};

  for (const spec of OPERATIONS_BY_ID.values()) {
    if (spec.kind !== 'scalar') continue;
    const raw = readPath(recipe, spec.path);
    if (typeof raw !== 'number') continue;
    if (Math.abs(raw - spec.neutral) < 1e-6) continue;
    active[spec.id] = Number(raw.toFixed(3));
  }

  if (recipe.temperature !== null) active.temperature = recipe.temperature;
  if (recipe.tint !== null) active.tint = recipe.tint;

  const curves: Record<string, string> = {};
  for (const channel of ['rgb', 'red', 'green', 'blue'] as const) {
    const description = describeCurve(recipe.curves[channel]);
    if (description !== 'neutral') curves[channel] = description;
  }
  if (Object.keys(curves).length > 0) active.curves = curves;

  if (recipe.masks.length > 0) {
    active.local_adjustments = recipe.masks.map((mask) => ({
      name: mask.name,
      kind: mask.kind,
      origin: mask.origin,
      enabled: mask.enabled,
      adjustments: Object.fromEntries(
        Object.entries(mask.adjustments).filter(([, value]) => value !== 0),
      ),
    }));
  }

  if (recipe.crop) active.crop = recipe.crop;
  if (recipe.orientation !== 0) active.orientation_quarter_turns = recipe.orientation;

  return active;
}
